import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShoppingBag, Minus, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { customerApi } from "../lib/api";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { VideoHero } from "../components/VideoHero";
import { RestockNotify } from "../components/RestockNotify";
import { useCart } from "../context/CartContext";
import { useCustomer } from "../context/CustomerContext";

// Per-product demo videos (keyed by product id). Add more products here as needed.
const PRODUCT_VIDEOS = {
  "70aa0312-9489-49fa-b1aa-d71bbd3d973c": {
    src: "https://customer-assets-39nsmqrw.emergentagent.net/wingman/4ff24718-b121-47ef-b578-026360a1848f/attachments/ba31f6b292534ee083e3a524a2cb8c5d_drift_covert_lizard_v3.mp4",
    label: "Watch It Swim",
    title: "COVERT™ — On The Water",
  },
};

const BASE_GROUPS = [
  { key: "dyes", label: "Dyes", group: "pigment", base: true },
  { key: "micas", label: "Mica Powder", group: "mica", base: true },
  { key: "pearls", label: "Pearl", group: "pearl", base: false },
  { key: "glitters", label: "Glitter", group: "glitter", base: false },
];

const emptyLayer = () => ({ dyes: [], micas: [], pearls: [], glitters: [] });

const Swatch = ({ hex }) =>
  hex ? (
    <span
      className="inline-block w-3.5 h-3.5 rounded-full mr-2 align-middle border border-white/25"
      style={{ backgroundColor: hex }}
    />
  ) : null;

// Multi-select recipe layer (used for single build + top/bottom split layers)
const LayerPicker = ({ libOptions, layer, onToggle, idPrefix }) => {
  const [q, setQ] = useState({});
  return (
    <div className="space-y-6">
      {BASE_GROUPS.map((g) => {
        const opts = libOptions[g.group] || [];
        if (!opts.length) return null;
        const selected = layer[g.key] || [];
        const term = (q[g.key] || "").toLowerCase().trim();
        const shown = term ? opts.filter((o) => o.name.toLowerCase().includes(term)) : opts;
        const searchable = opts.length > 16;
        return (
          <div key={g.key} data-testid={`${idPrefix}-${g.key}`}>
            <span className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">
              {g.label}
              {g.base && <span className="text-orange-500/80"> · counts as base</span>}
              {selected.length > 0 && <span className="text-orange-400/90"> · {selected.length} selected</span>}
            </span>
            {searchable && (
              <input
                type="text"
                value={q[g.key] || ""}
                onChange={(e) => setQ((s) => ({ ...s, [g.key]: e.target.value }))}
                placeholder={`Search ${g.label.toLowerCase()}…`}
                className="w-full bg-transparent border border-white/15 mt-3 px-3 py-2 focus:border-orange-500 focus:outline-none text-xs text-slate-200"
                data-testid={`${idPrefix}-${g.key}-search`}
              />
            )}
            <div className="flex flex-wrap gap-3 mt-3">
              {shown.map((v) => {
                const isSel = selected.includes(v.name);
                return (
                  <button
                    key={v.name}
                    onClick={() => v.in_stock && onToggle(g.key, v.name)}
                    disabled={!v.in_stock}
                    title={v.in_stock ? "" : "Back soon"}
                    className={`font-mono-plex text-xs tracking-wide px-4 py-2.5 border transition-colors ${
                      !v.in_stock
                        ? "border-white/5 text-slate-600 line-through cursor-not-allowed"
                        : isSel
                        ? "bg-orange-500/15 border-orange-500 text-white"
                        : "border-white/15 text-slate-400 hover:border-white/40"
                    }`}
                    data-testid={`${idPrefix}-${g.key}-${v.name}`}
                  >
                    <Swatch hex={v.hex} />
                    {v.name}
                    {isSel && <Check className="inline-block w-3 h-3 ml-2 text-orange-400 align-middle" />}
                    {!v.in_stock && <span className="ml-2 no-underline text-[9px] tracking-widest uppercase text-orange-500/70">back soon</span>}
                  </button>
                );
              })}
              {shown.length === 0 && (
                <span className="font-mono-plex text-xs text-slate-600">No matches</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { customer } = useCustomer();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [instructions, setInstructions] = useState("");
  const [libOptions, setLibOptions] = useState({ color: [], pigment: [], mica: [], sheen: [], pearl: [], glitter: [] });
  const [customTypes, setCustomTypes] = useState({});
  const [productType, setProductType] = useState(null);
  const [colorStock, setColorStock] = useState(null); // name -> in_stock (null = not loaded)
  const [colorHex, setColorHex] = useState({}); // name -> hex (finished colors, for regular products)
  const [colorMode, setColorMode] = useState("drift"); // custom pour: "drift" = pick a finished color, "build" = build your own
  const [recipeName, setRecipeName] = useState(""); // build-your-own recipe name
  const [split, setSplit] = useState(false); // build-your-own: split-color bait
  const [single, setSingle] = useState(emptyLayer); // non-split recipe layer
  const [topLayer, setTopLayer] = useState(emptyLayer); // split: top half
  const [bottomLayer, setBottomLayer] = useState(emptyLayer); // split: bottom half
  const [starterBlends, setStarterBlends] = useState([]); // admin-curated presets

  const mkToggle = (setter) => (key, name) =>
    setter((s) => ({ ...s, [key]: s[key].includes(name) ? s[key].filter((n) => n !== name) : [...s[key], name] }));
  const hasBase = (l) => (l.dyes.length + l.micas.length) > 0;
  const recapParts = (l) => {
    const p = [];
    if (l.dyes.length) p.push(`Dyes: ${l.dyes.join(", ")}`);
    if (l.micas.length) p.push(`Mica: ${l.micas.join(", ")}`);
    if (l.pearls.length) p.push(`Pearl: ${l.pearls.join(", ")}`);
    if (l.glitters.length) p.push(`Glitter: ${l.glitters.join(", ")}`);
    return p.join(" · ");
  };
  const hexOf = (name) => {
    for (const g of ["color", "pigment", "mica", "pearl", "glitter"]) {
      const found = (libOptions[g] || []).find((o) => o.name === name);
      if (found && found.hex) return found.hex;
    }
    return null;
  };
  const baseHexes = (l) => [...l.dyes, ...l.micas].map(hexOf).filter(Boolean);
  const fillStyle = (hexes) => {
    const cs = hexes.length ? hexes : ["#334155"];
    return cs.length === 1 ? { background: cs[0] } : { backgroundImage: `linear-gradient(90deg, ${cs.join(", ")})` };
  };
  const loadBlend = (b) => {
    const norm = (l) => ({ dyes: l?.dyes || [], micas: l?.micas || [], pearls: l?.pearls || [], glitters: l?.glitters || [] });
    setColorMode("build");
    setRecipeName(b.name && b.name !== "Untitled Blend" ? b.name : "");
    if (b.product_type && customTypes[b.product_type]) setProductType(b.product_type);
    const legacy = { dyes: [b.base_pigment].filter(Boolean), micas: [b.mica].filter(Boolean), pearls: [b.pearl].filter(Boolean), glitters: [b.glitter].filter(Boolean) };
    if (b.split) {
      setSplit(true);
      setTopLayer(norm(b.top));
      setBottomLayer(norm(b.bottom));
      setSingle(emptyLayer());
    } else {
      setSplit(false);
      const base = norm(b.base);
      setSingle((base.dyes.length + base.micas.length + base.pearls.length + base.glitters.length) ? base : legacy);
      setTopLayer(emptyLayer());
      setBottomLayer(emptyLayer());
    }
    toast.success(`Loaded "${b.name}" — tweak it your way`);
  };
  const layerToSpecs = (l, prefix = "") => {
    const o = {};
    if (l.dyes.length) o[`${prefix}Dyes`] = l.dyes;
    if (l.micas.length) o[`${prefix}Mica`] = l.micas;
    if (l.pearls.length) o[`${prefix}Pearl`] = l.pearls;
    if (l.glitters.length) o[`${prefix}Glitter`] = l.glitters;
    return o;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        // Full library (with stock) powers both regular + custom pages
        api
          .get("/options")
          .then((optRes) => {
            const grouped = { color: [], pigment: [], mica: [], sheen: [], pearl: [], glitter: [] };
            const cstock = {};
            const chex = {};
            optRes.data.forEach((o) => {
              if (grouped[o.group]) grouped[o.group].push({ name: o.name, in_stock: o.in_stock, hex: o.hex });
              if (o.group === "color") { cstock[o.name] = o.in_stock; chex[o.name] = o.hex; }
            });
            setLibOptions(grouped);
            setColorStock(cstock);
            setColorHex(chex);
            if (r.data.custom) {
              setColor((grouped.color.find((o) => o.in_stock) || {}).name || null);
            } else {
              const avail = (r.data.colors || []).filter((c) => cstock[c] !== false);
              if (avail.length) setColor(avail[0]);
            }
          })
          .catch(() => setColorStock({}));
        if (r.data.custom) {
          api.get("/custom-types").then((t) => {
            setCustomTypes(t.data);
            setProductType(Object.keys(t.data)[0] || null);
          }).catch(() => {});
          api.get("/starter-blends").then((b) => setStarterBlends(b.data)).catch(() => {});
        } else {
          setColor(r.data.colors?.[0] || null);
        }
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="bg-[#04070A] min-h-screen flex flex-col">
        <Nav />
        <div className="flex-1 flex flex-col items-center justify-center gap-6">
          <p className="font-display text-4xl uppercase">Bait not found</p>
          <Link to="/shop" className="font-mono-plex text-xs tracking-widest uppercase text-orange-500">← Back to shop</Link>
        </div>
      </div>
    );
  }

  if (!product) return <div className="bg-[#04070A] min-h-screen" />;

  const add = () => {
    if (product.custom) {
      const info = customTypes[productType] || {};
      const base = {
        ...(productType ? { "Product Type": productType } : {}),
        ...(info.length ? { Length: info.length } : {}),
        ...(info.pack_size ? { Pack: info.pack_size } : {}),
      };
      let cs;
      if (colorMode === "drift") {
        if (!color) {
          toast.error("Please pick a Drift color");
          return;
        }
        cs = { ...base, Color: color };
      } else if (split) {
        if (!hasBase(topLayer) || !hasBase(bottomLayer)) {
          toast.error("Pick at least one Dye or Mica for both the top and bottom");
          return;
        }
        cs = {
          ...base,
          Recipe: "Build your own — Split color",
          ...(recipeName.trim() ? { "Recipe Name": recipeName.trim() } : {}),
          ...layerToSpecs(topLayer, "Top — "),
          ...layerToSpecs(bottomLayer, "Bottom — "),
        };
      } else {
        if (!hasBase(single)) {
          toast.error("Select at least one Dye or Mica Powder as your base");
          return;
        }
        cs = {
          ...base,
          Recipe: "Build your own",
          ...(recipeName.trim() ? { "Recipe Name": recipeName.trim() } : {}),
          ...layerToSpecs(single),
        };
      }
      if (instructions.trim()) cs["Instructions"] = instructions.trim();
      addItem(product, null, qty, cs);
    } else {
      addItem(product, color, qty);
    }
    toast.success(`${product.name} added to cart`);
  };

  const saveRecipe = async () => {
    if (split ? (!hasBase(topLayer) || !hasBase(bottomLayer)) : !hasBase(single)) {
      toast.error("Select at least one Dye or Mica Powder as your base");
      return;
    }
    try {
      await customerApi.post("/customer/recipes", {
        name: recipeName.trim() || "My Custom Pour",
        product_type: productType || "",
        instructions: instructions.trim(),
        split,
        base: split ? emptyLayer() : single,
        top: split ? topLayer : emptyLayer(),
        bottom: split ? bottomLayer : emptyLayer(),
      });
      toast.success("Recipe saved to your account");
    } catch {
      toast.error("Could not save recipe");
    }
  };

  const soldOut = !product.in_stock || product.stock <= 0;
  const lowStock = !soldOut && product.stock <= 5;
  const typeInfo = product.custom ? (customTypes[productType] || {}) : null;
  const displayLength = product.custom ? typeInfo?.length : product.length;
  const displayPack = product.custom ? typeInfo?.pack_size : product.pack_size;
  const colorList = (product.colors || []).map((name) => ({ name, in_stock: colorStock ? colorStock[name] !== false : true, hex: colorHex[name] }));
  const driftColors = libOptions.color || [];

  return (
    <div className="bg-[#04070A] min-h-screen">
      <Nav />
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-32 pb-32">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 font-mono-plex text-xs tracking-widest uppercase text-slate-400 hover:text-orange-500 transition-colors mb-10"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20">
          <div>
            <motion.div
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
              className="relative overflow-hidden aspect-square bg-[#0A1118] border border-white/5"
            >
              <img src={product.images?.[activeImg]} alt={product.name} className="w-full h-full object-contain p-4" />
            </motion.div>
            {product.images?.length > 1 && (
              <div className="flex gap-3 mt-4">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`w-20 h-20 overflow-hidden border ${activeImg === i ? "border-orange-500" : "border-white/10"}`}
                    data-testid={`thumb-${i}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-contain p-1" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.19, 1, 0.22, 1] }}
          >
            <span className="font-mono-plex text-xs tracking-[0.25em] uppercase text-orange-500">{product.category}</span>
            <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-tighter mt-4 leading-[0.9]" data-testid="product-name">
              {product.name}
            </h1>
            <p className="font-display text-3xl text-orange-500 mt-6" data-testid="product-price">${product.price?.toFixed(2)}</p>
            {(displayLength || displayPack) && (
              <p className="font-mono-plex text-xs text-slate-500 tracking-wide mt-2">
                {[displayLength, displayPack].filter(Boolean).join(" · ")}
              </p>
            )}
            {lowStock && (
              <p className="font-mono-plex text-xs tracking-widest uppercase text-orange-400 mt-4" data-testid="detail-low-stock">
                ● Only {product.stock} left in stock
              </p>
            )}

            <p className="text-slate-300 text-lg leading-relaxed mt-8 max-w-lg">{product.description}</p>

            {product.custom && Object.keys(customTypes).length > 0 && (
              <div className="mt-10" data-testid="product-type-options">
                <span className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Product Type</span>
                <div className="flex flex-wrap gap-3 mt-3">
                  {Object.entries(customTypes).map(([type, info]) => (
                    <button
                      key={type}
                      onClick={() => setProductType(type)}
                      className={`text-left font-mono-plex text-xs tracking-wide px-4 py-2.5 border transition-colors ${
                        productType === type ? "bg-white/10 border-orange-500 text-white" : "border-white/15 text-slate-400 hover:border-white/40"
                      }`}
                      data-testid={`type-${type}`}
                    >
                      {type} <span className="text-slate-500">· {info.length} · {info.pack_size}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!product.custom && colorList?.length > 0 && (
              <div className="mt-10">
                <span className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Color</span>
                <div className="flex flex-wrap gap-3 mt-3" data-testid="color-options">
                  {colorList.map((c) => (
                    <button
                      key={c.name}
                      onClick={() => c.in_stock && setColor(c.name)}
                      disabled={!c.in_stock}
                      title={c.in_stock ? "" : "Back soon"}
                      className={`font-mono-plex text-xs tracking-wide px-4 py-2.5 border transition-colors ${
                        !c.in_stock
                          ? "border-white/5 text-slate-600 line-through cursor-not-allowed"
                          : color === c.name ? "bg-white/10 border-orange-500 text-white" : "border-white/15 text-slate-400 hover:border-white/40"
                      }`}
                      data-testid={`color-${c.name}`}
                    >
                      <Swatch hex={c.hex} />
                      {c.name}
                      {!c.in_stock && <span className="ml-2 no-underline text-[9px] tracking-widest uppercase text-orange-500/70">back soon</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {product.custom && (
              <div className="mt-10" data-testid="custom-options">
                {/* Choose a color path */}
                <span className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Color</span>
                <div className="flex flex-wrap gap-3 mt-3" data-testid="color-mode-toggle">
                  {[
                    { m: "drift", label: "Pick a Drift Color" },
                    { m: "build", label: "Build Your Own Recipe" },
                  ].map((o) => (
                    <button
                      key={o.m}
                      onClick={() => setColorMode(o.m)}
                      className={`font-mono-plex text-xs tracking-wide px-4 py-2.5 border transition-colors ${
                        colorMode === o.m ? "bg-orange-500 border-orange-500 text-[#04070A]" : "border-white/15 text-slate-400 hover:border-white/40"
                      }`}
                      data-testid={`color-mode-${o.m}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>

                {/* Starter blends — admin-curated one-tap presets */}
                {starterBlends.length > 0 && (
                  <div className="mt-6" data-testid="starter-blends">
                    <span className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">
                      Starter Blends <span className="text-slate-600">· tap to load &amp; tweak</span>
                    </span>
                    <div className="flex flex-wrap gap-3 mt-3">
                      {starterBlends.map((b) => {
                        const preview = b.split
                          ? baseHexes({ dyes: b.top?.dyes || [], micas: b.top?.micas || [] })
                          : baseHexes({ dyes: b.base?.dyes || [b.base_pigment].filter(Boolean), micas: b.base?.micas || [b.mica].filter(Boolean) });
                        return (
                          <button
                            key={b.id}
                            onClick={() => loadBlend(b)}
                            className="flex items-center gap-2 font-mono-plex text-xs tracking-wide px-3 py-2 border border-white/15 text-slate-300 hover:border-orange-500 hover:text-white transition-colors"
                            data-testid={`starter-blend-${b.id}`}
                          >
                            {b.split ? (
                              <span className="w-6 h-4 rounded-sm overflow-hidden border border-white/20 flex flex-col shrink-0">
                                <span className="flex-1" style={fillStyle(baseHexes({ dyes: b.top?.dyes || [], micas: b.top?.micas || [] }))} />
                                <span className="flex-1" style={fillStyle(baseHexes({ dyes: b.bottom?.dyes || [], micas: b.bottom?.micas || [] }))} />
                              </span>
                            ) : (
                              <span className="w-6 h-4 rounded-sm border border-white/20 shrink-0" style={fillStyle(preview)} />
                            )}
                            {b.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {colorMode === "drift" && (
                  <div className="mt-6" data-testid="drift-colors">
                    <span className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Drift Colors</span>
                    <div className="flex flex-wrap gap-3 mt-3">
                      {driftColors.map((c) => (
                        <button
                          key={c.name}
                          onClick={() => c.in_stock && setColor(c.name)}
                          disabled={!c.in_stock}
                          title={c.in_stock ? "" : "Back soon"}
                          className={`font-mono-plex text-xs tracking-wide px-4 py-2.5 border transition-colors ${
                            !c.in_stock
                              ? "border-white/5 text-slate-600 line-through cursor-not-allowed"
                              : color === c.name ? "bg-white/10 border-orange-500 text-white" : "border-white/15 text-slate-400 hover:border-white/40"
                          }`}
                          data-testid={`color-${c.name}`}
                        >
                          <Swatch hex={c.hex} />
                          {c.name}
                          {!c.in_stock && <span className="ml-2 no-underline text-[9px] tracking-widest uppercase text-orange-500/70">back soon</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Path B: build your own recipe */}
                {colorMode === "build" && (
                  <div data-testid="build-recipe">
                    <p className="mt-6 font-mono-plex text-[11px] tracking-wide text-slate-400 border-l-2 border-orange-500/60 pl-3 leading-relaxed" data-testid="build-base-note">
                      Select one or more <span className="text-orange-400">Dyes</span> or <span className="text-orange-400">Mica Powders</span> as your base — mix as many as you like. Pearl &amp; glitter are optional finishing effects.
                    </p>

                    <div className="mt-6">
                      <span className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Recipe Name <span className="text-slate-600">(optional)</span></span>
                      <input
                        type="text"
                        value={recipeName}
                        onChange={(e) => setRecipeName(e.target.value)}
                        placeholder="Name your blend — e.g. Sunset Bluegill"
                        className="w-full bg-transparent border border-white/15 mt-3 p-3 focus:border-orange-500 focus:outline-none text-sm text-slate-200"
                        data-testid="recipe-name"
                      />
                    </div>

                    {/* Split-color toggle */}
                    <button
                      onClick={() => setSplit((s) => !s)}
                      className={`mt-6 w-full flex items-center justify-between gap-3 px-4 py-3 border transition-colors ${
                        split ? "bg-orange-500/15 border-orange-500 text-white" : "border-white/15 text-slate-400 hover:border-white/40"
                      }`}
                      data-testid="split-toggle"
                    >
                      <span className="font-mono-plex text-xs tracking-wide text-left">
                        Make it a split-color bait
                        <span className="block text-[10px] text-slate-500 mt-0.5">Different color top &amp; bottom</span>
                      </span>
                      <span className={`w-9 h-5 rounded-full relative shrink-0 transition-colors ${split ? "bg-orange-500" : "bg-white/15"}`}>
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${split ? "left-4" : "left-0.5"}`} />
                      </span>
                    </button>

                    {!split && (
                      <div className="mt-6">
                        <LayerPicker libOptions={libOptions} layer={single} onToggle={mkToggle(setSingle)} idPrefix="single" />
                      </div>
                    )}

                    {split && (
                      <div className="mt-6 space-y-8">
                        <div className="border border-white/10 p-4" data-testid="split-top">
                          <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-orange-400">Top Color</span>
                          <div className="mt-4">
                            <LayerPicker libOptions={libOptions} layer={topLayer} onToggle={mkToggle(setTopLayer)} idPrefix="top" />
                          </div>
                        </div>
                        <div className="border border-white/10 p-4" data-testid="split-bottom">
                          <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-orange-400">Bottom Color</span>
                          <div className="mt-4">
                            <LayerPicker libOptions={libOptions} layer={bottomLayer} onToggle={mkToggle(setBottomLayer)} idPrefix="bottom" />
                          </div>
                        </div>
                      </div>
                    )}

                    {customer && (
                      <button
                        onClick={saveRecipe}
                        className="mt-8 w-full border border-orange-500/60 text-orange-400 font-mono-plex text-[10px] tracking-[0.2em] uppercase py-3 hover:bg-orange-500/10 transition-colors"
                        data-testid="save-recipe-button"
                      >
                        ♥ Save this recipe to my account
                      </button>
                    )}
                    {!customer && (
                      <p className="mt-8 font-mono-plex text-[10px] tracking-wide text-slate-500">
                        <Link to="/login" className="text-orange-400 hover:text-orange-300 underline">Sign in</Link> to save this recipe and reorder it anytime.
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-6">
                  <span className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Special Instructions</span>
                  <textarea
                    rows={4}
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    placeholder="Describe your custom pour — color blend, flake ratio, anything specific."
                    className="w-full bg-transparent border border-white/15 mt-3 p-3 focus:border-orange-500 focus:outline-none resize-none text-sm text-slate-200"
                    data-testid="custom-instructions"
                  />
                </div>
              </div>
            )}

            {product.custom && colorMode === "build" && (
              <div className="mt-8 border border-white/10 bg-[#0A1118] p-4" data-testid="blend-recap">
                <div className="flex items-center gap-3">
                  {split ? (
                    <span className="w-16 h-8 rounded-full overflow-hidden border border-white/20 flex flex-col shrink-0" data-testid="split-preview" title="Top / bottom preview">
                      <span className="flex-1" style={fillStyle(baseHexes(topLayer))} />
                      <span className="flex-1" style={fillStyle(baseHexes(bottomLayer))} />
                    </span>
                  ) : (
                    <span className="w-16 h-8 rounded-full border border-white/20 shrink-0" data-testid="blend-preview" style={fillStyle(baseHexes(single))} />
                  )}
                  <span className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-orange-500">Your blend</span>
                </div>
                {split ? (
                  <div className="mt-3 space-y-1 font-mono-plex text-xs text-slate-300">
                    <div><span className="text-orange-400/90">Top: </span>{recapParts(topLayer) || "—"}</div>
                    <div><span className="text-orange-400/90">Bottom: </span>{recapParts(bottomLayer) || "—"}</div>
                  </div>
                ) : (
                  <div className="mt-3 font-mono-plex text-xs text-slate-300">
                    {recapParts(single) || "Nothing selected yet"}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-stretch gap-4 mt-10">
              <div className="flex items-center border border-white/15">
                <button className="px-4" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-minus"><Minus className="w-4 h-4" /></button>
                <span className="px-4 font-mono-plex text-sm" data-testid="qty-value">{qty}</span>
                <button className="px-4" onClick={() => setQty((q) => q + 1)} data-testid="qty-plus"><Plus className="w-4 h-4" /></button>
              </div>
              <button
                onClick={add}
                disabled={soldOut}
                className="flex-1 bg-orange-500 text-[#04070A] font-mono-plex text-xs tracking-[0.2em] uppercase py-4 flex items-center justify-center gap-2 hover:bg-orange-600 active:scale-[0.98] transition-all disabled:opacity-40"
                data-testid="add-to-cart-button"
              >
                <ShoppingBag className="w-4 h-4" />
                {soldOut ? "Sold Out" : product.custom ? "Add Custom Request" : "Add to Cart"}
              </button>
            </div>

            {soldOut && !product.custom && (
              <div className="mt-4 border border-zinc-800 bg-[#0C1117] p-4" data-testid="restock-block">
                <p className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-3">
                  Sold out — get notified when we pour more
                </p>
                <RestockNotify productId={product.id} productName={product.name} mode="inline" />
              </div>
            )}

            <div className="mt-10 border-t border-white/5 pt-6" data-testid="drift-standard">
              <span className="font-mono-plex text-[10px] tracking-[0.25em] uppercase text-orange-500">The Drift Standard</span>
              <p className="text-slate-400 text-sm leading-relaxed mt-2 max-w-lg">
                Every bait has to earn its place. If we wouldn&rsquo;t fish it, we won&rsquo;t sell it.
              </p>
            </div>
          </motion.div>
        </div>

        {PRODUCT_VIDEOS[product.id] && (
          <div className="mt-24" data-testid="product-video">
            <div className="flex items-center gap-4 mb-6">
              <span className="font-mono-plex text-xs tracking-[0.25em] uppercase text-orange-500">See It In Action</span>
              <div className="h-px flex-1 bg-white/10" />
            </div>
            <VideoHero {...PRODUCT_VIDEOS[product.id]} className="border-y-0 border border-white/10" />
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
